import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CORD_START = { x: 124, y: 190 };
const CORD_END = { x: 124, y: 348 };

function buildGreeting(name, template) {
  return (template || "Selamat bekerja, {nama}").replace(/{nama}/g, name);
}

function speakGreeting(name, template) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const u = new SpeechSynthesisUtterance(buildGreeting(name, template));
  u.lang = "id-ID";
  u.rate = 0.95;
  u.pitch = 1.05;
  window.speechSynthesis.speak(u);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [on, setOn] = useState(false);
  const [shadeHue, setShadeHue] = useState(320);
  const [cord, setCord] = useState(CORD_END);
  const svgRef = useRef(null);
  const cordRef = useRef(CORD_END);
  const dragStart = useRef(null);
  const rafRef = useRef(null);

  const submit = async (e, creds) => {
    e?.preventDefault();
    const em = creds?.email || email;
    const pw = creds?.pass || password;
    setLoading(true);
    try {
      const loggedIn = await login(em, pw);
      if (loggedIn?.name) {
        sessionStorage.removeItem("sprintflow_greeted");
        speakGreeting(loggedIn.name, loggedIn.greeting_template);
      }
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  const toggleLamp = () => {
    setOn((prev) => !prev);
    const hue = Math.floor(Math.random() * 360);
    setShadeHue(hue);
    try {
      const click = new Audio("https://assets.codepen.io/605876/click.mp3");
      click.play().catch(() => {});
    } catch (_) {
      /* ignore */
    }
  };

  const snapCordBack = () => {
    cancelAnimationFrame(rafRef.current);
    const from = { ...cordRef.current };
    const t0 = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - t0) / 140);
      const next = {
        x: from.x + (CORD_END.x - from.x) * k,
        y: from.y + (CORD_END.y - from.y) * k,
      };
      cordRef.current = next;
      setCord(next);
      if (k < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const onCordPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    cancelAnimationFrame(rafRef.current);
    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const onCordPointerMove = (e) => {
    if (!dragStart.current || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) * 333) / rect.width + 10;
    const py = ((e.clientY - rect.top) * 484) / rect.height;
    const next = { x: px, y: Math.max(CORD_END.y, py) };
    cordRef.current = next;
    setCord(next);
  };

  const onCordPointerUp = (e) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    const travelled = Math.hypot(dx, dy);
    dragStart.current = null;
    snapCordBack();
    if (travelled > 50) toggleLamp();
  };

  const glowColor = `hsl(${shadeHue}, 40%, 45%)`;
  const glowColorDark = `hsl(${shadeHue}, 40%, 35%)`;

  return (
    <div
      className="lamp-scene"
      style={{
        "--on": on ? 1 : 0,
        "--shade-hue": shadeHue,
        "--glow-color": glowColor,
        "--glow-color-dark": glowColorDark,
      }}
    >
      <style>{LAMP_CSS}</style>

      <div className="container">
        <svg
          ref={svgRef}
          className="lamp"
          viewBox="0 0 333 484"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g className="lamp__shade shade">
            <ellipse className="shade__opening" cx="165" cy="220" rx="130" ry="20" />
            <ellipse className="shade__opening-shade" cx="165" cy="220" rx="130" ry="20" fill="url(#opening-shade)" />
          </g>
          <g className="lamp__base base">
            <path
              className="base__side"
              d="M165 464c44.183 0 80-8.954 80-20v-14h-22.869c-14.519-3.703-34.752-6-57.131-6-22.379 0-42.612 2.297-57.131 6H85v14c0 11.046 35.817 20 80 20z"
            />
            <path
              d="M165 464c44.183 0 80-8.954 80-20v-14h-22.869c-14.519-3.703-34.752-6-57.131-6-22.379 0-42.612 2.297-57.131 6H85v14c0 11.046 35.817 20 80 20z"
              fill="url(#side-shading)"
            />
            <ellipse className="base__top" cx="165" cy="430" rx="80" ry="20" />
            <ellipse cx="165" cy="430" rx="80" ry="20" fill="url(#base-shading)" />
          </g>
          <g className="lamp__post post">
            <path className="post__body" d="M180 142h-30v286c0 3.866 6.716 7 15 7 8.284 0 15-3.134 15-7V142z" />
            <path d="M180 142h-30v286c0 3.866 6.716 7 15 7 8.284 0 15-3.134 15-7V142z" fill="url(#post-shading)" />
          </g>
          <g className="lamp__cords cords" transform="translate(-10,0)">
            <line
              className="cord"
              x1={CORD_START.x}
              y1={CORD_START.y}
              x2={cord.x}
              y2={cord.y}
              strokeWidth="6"
              strokeLinecap="round"
            />
          </g>
          <path
            className="lamp__light"
            d="M290.5 193H39L0 463.5c0 11.046 75.478 20 165.5 20s167-11.954 167-23l-42-267.5z"
            fill="url(#light)"
          />
          <g className="lamp__top top">
            <path
              className="top__body"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M164.859 0c55.229 0 100 8.954 100 20l29.859 199.06C291.529 208.451 234.609 200 164.859 200S38.189 208.451 35 219.06L64.859 20c0-11.046 44.772-20 100-20z"
            />
            <path
              className="top__shading"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M164.859 0c55.229 0 100 8.954 100 20l29.859 199.06C291.529 208.451 234.609 200 164.859 200S38.189 208.451 35 219.06L64.859 20c0-11.046 44.772-20 100-20z"
              fill="url(#top-shading)"
            />
          </g>
          <g className="lamp__face face">
            <g className="lamp__mouth">
              <path d="M165 178c19.882 0 36-16.118 36-36h-72c0 19.882 16.118 36 36 36z" fill="#141414" />
              <clipPath className="lamp__feature" id="mouth" x="129" y="142" width="72" height="36">
                <path d="M165 178c19.882 0 36-16.118 36-36h-72c0 19.882 16.118 36 36 36z" fill="#141414" />
              </clipPath>
              <g clipPath="url(#mouth)">
                <circle className="lamp__tongue" cx="179.4" cy="172.6" r="18" />
              </g>
            </g>
            <g className="lamp__eyes">
              <path
                className="lamp__eye lamp__stroke"
                d="M115 135c0-5.523-5.82-10-13-10s-13 4.477-13 10"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transformBox: "fill-box",
                  transformOrigin: "center",
                  transform: on ? "rotate(0deg)" : "translateY(50%) rotate(180deg)",
                  transition: "transform .35s ease",
                }}
              />
              <path
                className="lamp__eye lamp__stroke"
                d="M241 135c0-5.523-5.82-10-13-10s-13 4.477-13 10"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transformBox: "fill-box",
                  transformOrigin: "center",
                  transform: on ? "rotate(0deg)" : "translateY(50%) rotate(180deg)",
                  transition: "transform .35s ease",
                }}
              />
            </g>
          </g>
          <defs>
            <linearGradient id="opening-shade" x1="35" y1="220" x2="295" y2="220" gradientUnits="userSpaceOnUse">
              <stop />
              <stop offset="1" stopColor="var(--shade)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="base-shading" x1="85" y1="444" x2="245" y2="444" gradientUnits="userSpaceOnUse">
              <stop stopColor="var(--b-1)" />
              <stop offset="0.8" stopColor="var(--b-2)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="side-shading" x1="119" y1="430" x2="245" y2="430" gradientUnits="userSpaceOnUse">
              <stop stopColor="var(--b-3)" />
              <stop offset="1" stopColor="var(--b-4)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="post-shading" x1="150" y1="288" x2="180" y2="288" gradientUnits="userSpaceOnUse">
              <stop stopColor="var(--b-1)" />
              <stop offset="1" stopColor="var(--b-2)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="light" x1="165.5" y1="218.5" x2="165.5" y2="483.5" gradientUnits="userSpaceOnUse">
              <stop stopColor="var(--l-1)" stopOpacity=".2" />
              <stop offset="1" stopColor="var(--l-2)" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="top-shading" x1="56" y1="110" x2="295" y2="110" gradientUnits="userSpaceOnUse">
              <stop stopColor="var(--t-1)" stopOpacity=".8" />
              <stop offset="1" stopColor="var(--t-2)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle
            className="lamp__hit"
            cx="124"
            cy="347"
            r="66"
            fill="#C4C4C4"
            fillOpacity=".1"
            transform="translate(-10,0)"
            onPointerDown={onCordPointerDown}
            onPointerMove={onCordPointerMove}
            onPointerUp={onCordPointerUp}
            onPointerCancel={onCordPointerUp}
          />
        </svg>

        <div className={`login-form ${on ? "active" : ""}`}>
          <h2>SuperProduction</h2>
          <p className="login-sub">Selamat Datang Kembali</p>
          <form onSubmit={submit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                placeholder="you@sprintflow.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading} data-testid="login-submit">
              {loading ? "Memproses..." : "Login"}
            </button>
          </form>
        </div>
      </div>

      {!on && <p className="lamp-hint">Tarik tali lampunya untuk menyalakan</p>}
    </div>
  );
}

const LAMP_CSS = `
  .lamp-scene {
    --cord: hsl(210, 0%, calc((40 + (var(--on, 0) * 50)) * 1%));
    --opening: hsl(50, calc((10 + (var(--on, 0) * 80)) * 1%), calc((20 + (var(--on, 0) * 70)) * 1%));
    --feature: #0a0a0a;
    --accent: 210;
    --tongue: #e06952;
    --base-top: hsl(var(--accent), 0%, calc((40 + (var(--on, 0) * 40)) * 1%));
    --base-side: hsl(var(--accent), 0%, calc((20 + (var(--on, 0) * 40)) * 1%));
    --post: hsl(var(--accent), 0%, calc((20 + (var(--on, 0) * 40)) * 1%));
    --b-1: hsla(45, 0%, calc((50 + (var(--on, 0) * 50)) * 1%), 0.85);
    --b-2: hsla(45, 0%, calc((20 + (var(--on, 0) * 30)) * 1%), 0.25);
    --b-3: hsla(45, 0%, calc((20 + (var(--on, 0) * 30)) * 1%), 0.5);
    --b-4: hsla(45, 0%, calc((20 + (var(--on, 0) * 30)) * 1%), 0.25);
    --l-1: hsla(45, calc((0 + (var(--on, 0) * 20)) * 1%), calc((50 + (var(--on, 0) * 50)) * 1%), 0.85);
    --l-2: hsla(45, calc((0 + (var(--on, 0) * 20)) * 1%), calc((50 + (var(--on, 0) * 50)) * 1%), 0.85);
    --t-1: hsl(var(--shade-hue, 320), calc((0 + (var(--on, 0) * 20)) * 1%), calc((30 + (var(--on, 0) * 60)) * 1%));
    --t-2: hsl(var(--shade-hue, 320), calc((0 + (var(--on, 0) * 20)) * 1%), calc((20 + (var(--on, 0) * 35)) * 1%));
    --t-3: hsl(var(--shade-hue, 320), calc((0 + (var(--on, 0) * 20)) * 1%), calc((10 + (var(--on, 0) * 20)) * 1%));
    --glow-color: hsl(320, 40%, 45%);
    --glow-color-dark: hsl(320, 40%, 35%);
    min-height: 100vh;
    display: grid;
    place-items: center;
    background: #121921;
    margin: 0;
    font-family: system-ui, -apple-system, sans-serif;
    position: relative;
    overflow: hidden;
  }

  .lamp-scene .container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8vmin;
    flex-wrap: wrap;
    padding: 2rem;
  }

  .lamp-scene .login-form {
    background: rgba(18, 25, 33, 0.9);
    padding: 2.75rem 3rem;
    border-radius: 20px;
    width: 100%;
    max-width: 440px;
    opacity: 0;
    transform: scale(0.8) translateY(20px);
    pointer-events: none;
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 0 0px rgba(255, 255, 255, 0);
  }

  .lamp-scene .login-form.active {
    opacity: 1;
    transform: scale(1) translateY(0);
    pointer-events: all;
    border-color: var(--glow-color);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.1), 0 0 30px var(--glow-color), inset 0 0 15px rgba(255, 255, 255, 0.05);
  }

  .lamp-scene .login-form h2 {
    color: #fff;
    font-size: 2rem;
    margin: 0;
    text-align: center;
    text-shadow: 0 0 8px var(--glow-color);
  }

  .lamp-scene .login-sub {
    color: #888;
    font-size: 0.95rem;
    text-align: center;
    margin: 0.25rem 0 2rem 0;
    text-shadow: 0 0 5px var(--glow-color);
  }

  .lamp-scene .form-group {
    margin-bottom: 1.5rem;
  }

  .lamp-scene .form-group label {
    display: block;
    color: #aaa;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    text-shadow: 0 0 5px var(--glow-color);
  }

  .lamp-scene .form-group input {
    width: 100%;
    padding: 0.75rem 1rem;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: #fff;
    font-size: 1rem;
    transition: all 0.3s ease;
  }

  .lamp-scene .form-group input:focus {
    outline: none;
    border-color: var(--glow-color);
    box-shadow: 0 0 10px var(--glow-color);
    background: rgba(255, 255, 255, 0.08);
  }

  .lamp-scene .form-group input::placeholder {
    color: #666;
  }

  .lamp-scene .login-btn {
    width: 100%;
    padding: 0.875rem;
    background: linear-gradient(135deg, var(--glow-color), var(--glow-color-dark));
    border: none;
    border-radius: 10px;
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    margin-top: 0.5rem;
  }

  .lamp-scene .login-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3), 0 0 20px var(--glow-color);
  }

  .lamp-scene .login-btn:active:not(:disabled) {
    transform: translateY(0);
  }

  .lamp-scene .login-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .lamp-scene .lamp {
    height: 40vmin;
    overflow: visible !important;
    touch-action: none;
    user-select: none;
  }

  .lamp-scene .cord {
    stroke: var(--cord);
  }

  .lamp-scene .lamp__tongue {
    fill: var(--tongue);
  }

  .lamp-scene .lamp__hit {
    cursor: grab;
    opacity: 0;
  }

  .lamp-scene .lamp__hit:active {
    cursor: grabbing;
  }

  .lamp-scene .lamp__feature {
    fill: var(--feature);
  }

  .lamp-scene .lamp__stroke {
    stroke: var(--feature);
  }

  .lamp-scene .lamp__mouth,
  .lamp-scene .lamp__light {
    opacity: var(--on, 0);
  }

  .lamp-scene .shade__opening {
    fill: var(--opening);
  }

  .lamp-scene .shade__opening-shade {
    opacity: calc(1 - var(--on, 0));
  }

  .lamp-scene .post__body {
    fill: var(--post);
  }

  .lamp-scene .base__top {
    fill: var(--base-top);
  }

  .lamp-scene .base__side {
    fill: var(--base-side);
  }

  .lamp-scene .top__body {
    fill: var(--t-3);
  }

  .lamp-scene .lamp-hint {
    position: absolute;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    color: #556;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
    animation: hint-pulse 2s ease-in-out infinite;
  }

  @keyframes hint-pulse {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 0.9; }
  }
`;
